import React, { useState, useEffect } from 'react';
import { propertyAPI, bookingTemplateAPI, developerTemplateAPI, categoryAPI, getUploadUrl, default as api } from '../../lib/api';
import { formatCurrency, formatCommissionRate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Building2, Plus, Edit, Trash2, Image, FileText, MapPin, Search, ArrowUpDown } from 'lucide-react';
import { PropertyFormDialog } from './properties/PropertyFormDialog';

const DEFAULT_FORM = {
  name: '', description: '', location: '', state: '', city: '', address: '', postcode: '', tenure_type: '', developer: '',
  property_type: 'condominium', total_units: '', price_range_min: '', price_range_max: '',
  is_exclusive: true, images: [], brochure_url: null, layouts: [], sales_kits: [], booking_template_id: '', developer_template_id: '',
  amenities: [], commission_percentage: '3',
  commission_mode: 'percentage', commission_fixed_amount: '',
  pic_commission_percentage: '7',
  pic_assignments: [], pic_user_ids: [],
  fast_comm_options: { '0': true, '50': true, '100': true },
  commission_schedules: [],
  agent_commission_split: { agent: '40', rm: '45', avp: '50', svp: '55', evp: '60' },
  customer_cashback_percentage: '0.5', introducer_commission_percentage: '0.5',
  platform_fee_percentage: '0.5', referral_fee_percentage: '0', pic_user_id: '', total_booking_fee: '',
};

const AdminProperties = () => {
  const [properties, setProperties] = useState([]);
  const [bookingTemplates, setBookingTemplates] = useState([]);
  const [developerTemplates, setDeveloperTemplates] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterDeveloper, setFilterDeveloper] = useState('all');
  const [filterState, setFilterState] = useState('all');
  const [sortBy, setSortBy] = useState('created_desc');
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [amenitiesInput, setAmenitiesInput] = useState('');
  const [pendingImages, setPendingImages] = useState([]);
  const [pendingBrochure, setPendingBrochure] = useState(null);
  const [pendingLayouts, setPendingLayouts] = useState([]);
  const [pendingSalesKits, setPendingSalesKits] = useState([]);
  const [formCities, setFormCities] = useState([]);

  useEffect(() => { loadProperties(); loadBookingTemplates(); loadDeveloperTemplates(); loadCategories(); }, []);
  useEffect(() => { loadProperties(); }, [searchText, filterDeveloper, filterState, sortBy]);
  useEffect(() => { setFormCities(formData.state ? cities.filter(c => c.state === formData.state) : []); }, [formData.state, cities]);

  const loadProperties = async () => {
    try {
      const params = {};
      if (searchText) params.search = searchText;
      if (filterDeveloper !== 'all') params.developer = filterDeveloper;
      if (filterState !== 'all') params.state = filterState;
      if (sortBy) params.sort_by = sortBy;
      const res = await propertyAPI.getProperties(params);
      setProperties(res.data);
    } catch { toast.error('Failed to load properties'); } finally { setLoading(false); }
  };
  const loadBookingTemplates = async () => { try { const res = await bookingTemplateAPI.getTemplates(); setBookingTemplates(res.data || []); } catch {} };
  const loadDeveloperTemplates = async () => { try { const res = await developerTemplateAPI.getTemplates(); setDeveloperTemplates(res.data || []); } catch {} };
  const loadCategories = async () => {
    try {
      const [devRes, stateRes, cityRes] = await Promise.all([
        categoryAPI.getCategories({ type: 'developer' }), categoryAPI.getCategories({ type: 'state' }), categoryAPI.getCategories({ type: 'city' }),
      ]);
      setDevelopers(devRes.data || []); setStates(stateRes.data || []); setCities(cityRes.data || []);
    } catch {}
  };

  const closeModal = () => { setShowModal(false); setEditingProperty(null); setFormData(DEFAULT_FORM); setAmenitiesInput(''); setPendingImages([]); setPendingBrochure(null); setPendingLayouts([]); setPendingSalesKits([]); };

  const handleSubmit = async () => {
    if (!formData.name || !formData.developer) { toast.error('Please fill in required fields (Name, Developer)'); return; }
    setUploading(true);
    try {
      const locationStr = [formData.city, formData.state].filter(Boolean).join(', ');
      const data = {
        name: formData.name, description: formData.description, location: locationStr || formData.location,
        state: formData.state || null, city: formData.city || null, address: formData.address || null,
        postcode: formData.postcode || null, tenure_type: formData.tenure_type || null,
        developer: formData.developer, property_type: formData.property_type,
        total_units: parseInt(formData.total_units) || 0, price_range_min: parseFloat(formData.price_range_min) || 0,
        price_range_max: parseFloat(formData.price_range_max) || 0, is_exclusive: formData.is_exclusive,
        commission_percentage: parseFloat(formData.commission_percentage) || 3,
        commission_mode: formData.commission_mode || 'percentage',
        commission_fixed_amount: formData.commission_fixed_amount ? parseFloat(formData.commission_fixed_amount) : 0,
        pic_commission_percentage: formData.pic_commission_percentage !== '' && formData.pic_commission_percentage !== null && formData.pic_commission_percentage !== undefined
          ? parseFloat(formData.pic_commission_percentage)
          : 7,
        pic_assignments: (formData.pic_assignments || []).map(a => ({
          user_id: a.user_id,
          tier: a.tier || 'primary',
          split_mode: a.split_mode || 'percentage',
          split_value: parseFloat(a.split_value) || 0,
        })).filter(a => a.user_id),
        pic_user_ids: (formData.pic_user_ids && formData.pic_user_ids.length)
          ? formData.pic_user_ids
          : (formData.pic_assignments || []).map(a => a.user_id).filter(Boolean),
        fast_comm_options: formData.fast_comm_options || { '0': true, '50': true, '100': true },
        commission_schedules: (formData.commission_schedules || []).map(s => ({
          id: s.id || undefined,
          effective_from: s.effective_from,
          commission_mode: s.commission_mode || 'percentage',
          commission_percentage: s.commission_percentage !== '' && s.commission_percentage !== undefined && s.commission_percentage !== null ? parseFloat(s.commission_percentage) : null,
          commission_fixed_amount: s.commission_fixed_amount !== '' && s.commission_fixed_amount !== undefined && s.commission_fixed_amount !== null ? parseFloat(s.commission_fixed_amount) : null,
          note: s.note || '',
        })).filter(s => s.effective_from),
        customer_cashback_percentage: parseFloat(formData.customer_cashback_percentage) || 0.5,
        introducer_commission_percentage: parseFloat(formData.introducer_commission_percentage) || 0.5,
        platform_fee_percentage: parseFloat(formData.platform_fee_percentage) || 0.5,
        referral_fee_percentage: parseFloat(formData.referral_fee_percentage) || 0,
        pic_user_id: formData.pic_user_id || '', total_booking_fee: formData.total_booking_fee ? parseFloat(formData.total_booking_fee) : null,
        agent_commission_split: Object.fromEntries(Object.entries(formData.agent_commission_split).map(([k, v]) => [k, parseFloat(v) || 50])),
        amenities: amenitiesInput.split(',').map(a => a.trim()).filter(a => a),
        images: formData.images || [], brochure_url: formData.brochure_url,
        booking_template_id: formData.booking_template_id || null, developer_template_id: formData.developer_template_id || null,
      };
      let propertyId;
      if (editingProperty) { await propertyAPI.updateProperty(editingProperty.id, data); propertyId = editingProperty.id; toast.success('Property updated'); }
      else { const res = await propertyAPI.createProperty(data); propertyId = res.data.id; toast.success('Property created'); }
      if (pendingImages.length > 0 && propertyId) { try { await propertyAPI.uploadImages(propertyId, pendingImages); } catch {} }
      if (pendingBrochure && propertyId) { try { await propertyAPI.uploadBrochure(propertyId, pendingBrochure); } catch {} }
      if (pendingLayouts.length > 0 && propertyId) { try { await propertyAPI.uploadLayouts(propertyId, pendingLayouts); } catch (e) { toast.error('Some layouts failed to upload'); } }
      if (pendingSalesKits.length > 0 && propertyId) { try { await propertyAPI.uploadSalesKits(propertyId, pendingSalesKits); } catch (e) { toast.error('Some sales kit files failed to upload'); } }
      closeModal(); loadProperties();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save property'); } finally { setUploading(false); }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({ ...DEFAULT_FORM, ...property, is_exclusive: property.is_exclusive !== false, state: property.state || '', city: property.city || '', address: property.address || '', postcode: property.postcode || '', tenure_type: property.tenure_type || '',
      total_booking_fee: property.total_booking_fee?.toString() || '', commission_percentage: property.commission_percentage?.toString() || '3',
      commission_mode: property.commission_mode || 'percentage',
      commission_fixed_amount: property.commission_fixed_amount?.toString() || '',
      pic_commission_percentage: property.pic_commission_percentage !== undefined && property.pic_commission_percentage !== null
        ? property.pic_commission_percentage.toString()
        : '7',
      pic_assignments: Array.isArray(property.pic_assignments) ? property.pic_assignments : [],
      pic_user_ids: Array.isArray(property.pic_user_ids) ? property.pic_user_ids : [],
      fast_comm_options: property.fast_comm_options || { '0': true, '50': true, '100': true },
      commission_schedules: Array.isArray(property.commission_schedules) ? property.commission_schedules : [],
      layouts: Array.isArray(property.layouts) ? property.layouts : [],
      sales_kits: Array.isArray(property.sales_kits) ? property.sales_kits : [],
      customer_cashback_percentage: property.customer_cashback_percentage?.toString() || '0.5', introducer_commission_percentage: property.introducer_commission_percentage?.toString() || '0.5',
      platform_fee_percentage: property.platform_fee_percentage?.toString() || '0.5', referral_fee_percentage: property.referral_fee_percentage?.toString() || '0',
      pic_user_id: property.pic_user_id || '', total_units: property.total_units?.toString() || '', price_range_min: property.price_range_min?.toString() || '',
      price_range_max: property.price_range_max?.toString() || '', booking_template_id: property.booking_template_id || '', developer_template_id: property.developer_template_id || '',
      agent_commission_split: Object.fromEntries(Object.entries(property.agent_commission_split || DEFAULT_FORM.agent_commission_split).map(([k, v]) => [k, v?.toString() || '50'])),
    });
    setAmenitiesInput(property.amenities?.join(', ') || ''); setPendingImages([]); setPendingBrochure(null); setPendingLayouts([]); setPendingSalesKits([]); setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this property? This will also delete all units.')) return;
    try { await propertyAPI.deleteProperty(id); toast.success('Property deleted'); loadProperties(); } catch { toast.error('Failed to delete property'); }
  };
  const removeExistingImage = async (imageIndex) => {
    if (!editingProperty) return; if (!window.confirm('Remove this image?')) return;
    try { await propertyAPI.deleteImage(editingProperty.id, imageIndex); const newImages = [...formData.images]; newImages.splice(imageIndex, 1); setFormData({ ...formData, images: newImages }); toast.success('Image removed'); } catch { toast.error('Failed'); }
  };
  const removeExistingBrochure = async () => {
    if (!editingProperty) return; if (!window.confirm('Remove this brochure?')) return;
    try { await propertyAPI.deleteBrochure(editingProperty.id); setFormData({ ...formData, brochure_url: null }); toast.success('Brochure removed'); } catch { toast.error('Failed'); }
  };
  const removeExistingLayout = async (layoutIndex) => {
    if (!editingProperty) return; if (!window.confirm('Remove this layout?')) return;
    try {
      await propertyAPI.deleteLayout(editingProperty.id, layoutIndex);
      const newLayouts = [...(formData.layouts || [])];
      newLayouts.splice(layoutIndex, 1);
      setFormData({ ...formData, layouts: newLayouts });
      toast.success('Layout removed');
    } catch { toast.error('Failed to remove layout'); }
  };
  const removeExistingSalesKit = async (kitId) => {
    if (!editingProperty) return; if (!window.confirm('Remove this sales kit file?')) return;
    try {
      await propertyAPI.deleteSalesKit(editingProperty.id, kitId);
      setFormData({ ...formData, sales_kits: (formData.sales_kits || []).filter(k => k.id !== kitId) });
      toast.success('File removed');
    } catch { toast.error('Failed to remove file'); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const allDeveloperNames = [...new Set([...developers.map(d => d.name), ...properties.map(p => p.developer).filter(Boolean)])].sort();

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-properties">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-gray-900">Property Management</h1><p className="text-gray-500 mt-1">Manage property listings and details</p></div>
        <PropertyFormDialog
          open={showModal} onOpenChange={o => { if (!o) closeModal(); else setShowModal(true); }}
          editing={editingProperty} formData={formData} setFormData={setFormData}
          amenitiesInput={amenitiesInput} setAmenitiesInput={setAmenitiesInput}
          pendingImages={pendingImages} setPendingImages={setPendingImages}
          pendingBrochure={pendingBrochure} setPendingBrochure={setPendingBrochure}
          pendingLayouts={pendingLayouts} setPendingLayouts={setPendingLayouts}
          pendingSalesKits={pendingSalesKits} setPendingSalesKits={setPendingSalesKits}
          formCities={formCities} developers={developers} states={states}
          bookingTemplates={bookingTemplates} developerTemplates={developerTemplates}
          uploading={uploading} onSubmit={handleSubmit}
          onRemoveExistingImage={removeExistingImage} onRemoveExistingBrochure={removeExistingBrochure}
          onRemoveExistingLayout={removeExistingLayout} onRemoveExistingSalesKit={removeExistingSalesKit}
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search project name, developer, location..." value={searchText} onChange={e => setSearchText(e.target.value)} className="pl-10" data-testid="property-search" />
        </div>
        <Select value={filterDeveloper} onValueChange={setFilterDeveloper}>
          <SelectTrigger className="w-48" data-testid="filter-developer"><SelectValue placeholder="All Developers" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Developers</SelectItem>{allDeveloperNames.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filterState} onValueChange={setFilterState}>
          <SelectTrigger className="w-44" data-testid="filter-state"><SelectValue placeholder="All States" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All States</SelectItem>{states.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-44" data-testid="sort-by"><ArrowUpDown className="w-3.5 h-3.5 mr-1.5" /><SelectValue placeholder="Sort by" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="created_desc">Newest First</SelectItem><SelectItem value="created_asc">Oldest First</SelectItem>
            <SelectItem value="name_asc">Name A-Z</SelectItem><SelectItem value="name_desc">Name Z-A</SelectItem>
            <SelectItem value="updated_desc">Recently Updated</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {properties.length > 0 ? (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Property</TableHead><TableHead className="font-semibold">Developer</TableHead>
                <TableHead className="font-semibold">Location</TableHead><TableHead className="font-semibold">Type</TableHead>
                <TableHead className="font-semibold">Price Range</TableHead><TableHead className="font-semibold text-center">Units</TableHead>
                <TableHead className="font-semibold text-center">Commission</TableHead><TableHead className="font-semibold">Created</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {properties.map(p => (
                <TableRow key={p.id} className="hover:bg-gray-50" data-testid={`property-row-${p.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                        {p.images?.length > 0 ? <img src={getUploadUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} /> : <Building2 className="w-6 h-6 text-gray-400" />}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{p.name}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          {p.images?.length > 0 && <Badge variant="outline" className="text-xs px-1.5 py-0"><Image className="w-3 h-3 mr-0.5" />{p.images.length}</Badge>}
                          {p.brochure_url && <Badge variant="outline" className="text-xs text-blue-600 px-1.5 py-0"><FileText className="w-3 h-3" /></Badge>}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><span className="text-gray-700 text-sm">{p.developer}</span></TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {p.state && p.city ? <div><span className="text-gray-900">{p.city}</span><span className="text-gray-400 ml-1 text-xs">{p.state}</span></div> : p.location ? <span className="text-gray-600">{p.location}</span> : <span className="text-gray-300">-</span>}
                      {p.address && <div className="flex items-start gap-1 mt-0.5"><MapPin className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" /><span className="text-gray-400 text-xs line-clamp-1">{p.address}</span></div>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{p.property_type}</Badge>
                    <Badge className={`ml-1 text-xs ${p.is_exclusive !== false ? 'bg-[#4E50FF]/10 text-[#4E50FF] border border-[#4E50FF]/30' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>{p.is_exclusive !== false ? 'Excl' : 'Non-Excl'}</Badge>
                  </TableCell>
                  <TableCell className="text-gray-900 font-mono text-sm">{formatCurrency(p.price_range_min)} - {formatCurrency(p.price_range_max)}</TableCell>
                  <TableCell className="text-center text-gray-600">{p.total_units}</TableCell>
                  <TableCell className="text-center"><Badge className="bg-amber-100 text-amber-700">{formatCommissionRate(p)}</Badge></TableCell>
                  <TableCell className="text-gray-500 text-xs"><div>{fmtDate(p.created_at)}</div>{p.updated_at && <div className="text-gray-400">Upd: {fmtDate(p.updated_at)}</div>}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(p)} data-testid={`edit-${p.id}`}><Edit className="w-4 h-4" /></Button>
                      {p.is_archived ? (
                        <Button
                          size="sm" variant="ghost"
                          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          title="Re-open (un-archive) this property"
                          onClick={async () => {
                            try { await api.post(`/properties/${p.id}/unarchive`); toast.success('Property re-opened'); loadProperties(); }
                            catch (e) { toast.error(e.response?.data?.detail || 'Failed to re-open'); }
                          }}
                          data-testid={`unarchive-${p.id}`}
                        >Re-open</Button>
                      ) : (
                        <Button
                          size="sm" variant="ghost"
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          title="Archive (hide from mobile listings, keep history)"
                          onClick={async () => {
                            if (!window.confirm(`Archive "${p.name}"? It will be hidden from mobile listings but history stays intact. You can re-open it later.`)) return;
                            try { await api.post(`/properties/${p.id}/archive`); toast.success('Property archived'); loadProperties(); }
                            catch (e) { toast.error(e.response?.data?.detail || 'Failed to archive'); }
                          }}
                          data-testid={`archive-${p.id}`}
                        >Archive</Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(p.id)} data-testid={`delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Properties</h3>
          <p className="text-gray-500 mb-4">Add your first property to get started</p>
          <Button onClick={() => setShowModal(true)} className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-2" /> Add Property</Button>
        </div>
      )}
    </div>
  );
};

export default AdminProperties;
